// Fal.AI client — Text-to-SVG and Image-to-SVG endpoints.
// API key stored in localStorage; user provides their own.
//
// Endpoints used:
//   - Text → SVG: fal-ai/recraft/v3/text-to-image (svg style)
//   - Image → SVG: fal-ai/imageutils/svg-to-png reversed via fal-ai/recraft/v3/image-to-image
// Falls back to a SAMPLE pattern generator when API key is missing so the UI is testable.

export interface FalConfig {
  apiKey: string;
  textModel: string;     // e.g. 'fal-ai/recraft/v3/text-to-image'
  imageModel: string;    // e.g. 'fal-ai/recraft/v3/image-to-image' (returns rasterized SVG-styled output)
}

const KEY_STORAGE = 'clayforge.falApiKey';

export function loadFalConfig(): FalConfig {
  return {
    apiKey: localStorage.getItem(KEY_STORAGE) ?? '',
    textModel: localStorage.getItem('clayforge.falTextModel') ?? 'fal-ai/recraft/v3/text-to-image',
    imageModel: localStorage.getItem('clayforge.falImageModel') ?? 'fal-ai/recraft/v3/image-to-image'
  };
}

export function saveFalApiKey(key: string): void {
  if (key) localStorage.setItem(KEY_STORAGE, key);
  else localStorage.removeItem(KEY_STORAGE);
}

export interface SvgGenerationResult {
  svg: string;          // raw SVG markup
  source: 'fal' | 'mock';
  prompt: string;
  imageUrl?: string;    // optional preview if Fal returned a raster
}

const SYSTEM_PROMPT = `You are designing a tileable monochrome surface pattern for a 3D-printed clay vase.
The pattern will be applied as a height-displacement texture: WHITE areas push outward, BLACK areas push inward, GREY (#808080) is neutral.
Hard requirements:
- Output must be a single SVG (<svg> root) with viewBox="0 0 64 64".
- Use only fill="white", fill="black", and a base background rect of fill="#808080".
- The pattern MUST tile seamlessly along both X and Y (no half-shapes at edges).
- Avoid thin strokes < 1.5 units. Strokes must be filled, not stroked.
- Strong contrast, geometric, repeatable.
- Return ONLY the SVG. No explanation, no markdown.`;

const STYLE_PRESETS = [
  { id: 'geometric', label: 'Geometrik', extra: 'Sharp angular shapes, minimal, modernist.' },
  { id: 'organic',   label: 'Organik',   extra: 'Flowing curves, leaf-like, botanical.' },
  { id: 'tribal',    label: 'Etnik',     extra: 'Tribal motifs, repeating folk symbols, cultural.' },
  { id: 'fractal',   label: 'Fraktal',   extra: 'Self-similar, recursive, mandala-like.' },
  { id: 'mosaic',    label: 'Mozaik',    extra: 'Tessellated tiles, herringbone or hexagonal mosaic.' },
  { id: 'celestial', label: 'Göksel',    extra: 'Stars, moons, suns, constellations.' }
] as const;

export type StylePresetId = typeof STYLE_PRESETS[number]['id'];
export const PROMPT_PRESETS = STYLE_PRESETS;

/** Build the final prompt sent to Fal. */
export function buildPrompt(userPrompt: string, style: StylePresetId | null): string {
  const stylePreset = style ? STYLE_PRESETS.find((s) => s.id === style) : null;
  const styleAddon = stylePreset ? `\nStyle preset: ${stylePreset.extra}` : '';
  return `${SYSTEM_PROMPT}\n\nUser request: ${userPrompt}${styleAddon}`;
}

/**
 * Text-to-SVG generation. If no API key is configured, returns a deterministic
 * mock SVG so UI is testable end-to-end without a key.
 */
export async function generateSvgFromText(
  userPrompt: string,
  style: StylePresetId | null = null
): Promise<SvgGenerationResult> {
  const cfg = loadFalConfig();
  const fullPrompt = buildPrompt(userPrompt, style);

  if (!cfg.apiKey) {
    return {
      svg: mockPatternForPrompt(userPrompt, style),
      source: 'mock',
      prompt: fullPrompt
    };
  }

  // Fal.AI synchronous endpoint — small, returns image_url field for rasters.
  // Recraft v3 supports svg style: substyle: 'vector_illustration'.
  const res = await fetch(`https://fal.run/${cfg.textModel}`, {
    method: 'POST',
    headers: {
      'Authorization': `Key ${cfg.apiKey}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      prompt: fullPrompt,
      style: 'vector_illustration',
      image_size: { width: 512, height: 512 }
    })
  });
  if (!res.ok) throw new Error(`Fal.AI ${res.status}: ${await res.text()}`);
  const data = await res.json();
  const imageUrl = data.images?.[0]?.url ?? data.image?.url;
  if (!imageUrl) throw new Error('Fal.AI did not return an image URL');
  // Fetch and inline as base64 so the resulting SVG works cross-origin in canvas.
  const inlinedDataUrl = await fetchAsDataUrl(imageUrl);
  const wrappedSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64"><image href="${inlinedDataUrl}" width="64" height="64"/></svg>`;
  return { svg: wrappedSvg, source: 'fal', prompt: fullPrompt, imageUrl };
}

/**
 * Image-to-SVG generation: send an uploaded image plus an optional textual nudge,
 * receive a stylized vectorish image we wrap as an SVG <image> for our pipeline.
 */
export async function generateSvgFromImage(
  imageFile: File,
  userExtra: string,
  style: StylePresetId | null = null
): Promise<SvgGenerationResult> {
  const cfg = loadFalConfig();
  const fullPrompt = buildPrompt(
    `Convert the supplied image into a tileable monochrome surface pattern. ${userExtra}`,
    style
  );

  if (!cfg.apiKey) {
    return {
      svg: mockPatternForImage(imageFile.name, userExtra, style),
      source: 'mock',
      prompt: fullPrompt
    };
  }

  // Upload the image as base64 to Fal — most endpoints accept image_url with data URLs.
  const dataUrl = await fileToDataUrl(imageFile);
  const res = await fetch(`https://fal.run/${cfg.imageModel}`, {
    method: 'POST',
    headers: {
      'Authorization': `Key ${cfg.apiKey}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      image_url: dataUrl,
      prompt: fullPrompt,
      style: 'vector_illustration',
      strength: 0.85
    })
  });
  if (!res.ok) throw new Error(`Fal.AI ${res.status}: ${await res.text()}`);
  const data = await res.json();
  const imageUrl = data.images?.[0]?.url ?? data.image?.url;
  if (!imageUrl) throw new Error('Fal.AI did not return an image URL');
  const inlinedDataUrl = await fetchAsDataUrl(imageUrl);
  const wrappedSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64"><image href="${inlinedDataUrl}" width="64" height="64"/></svg>`;
  return { svg: wrappedSvg, source: 'fal', prompt: fullPrompt, imageUrl };
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/** Fetch a remote image and inline it as a data: URL (so SVG <image> works cross-origin). */
async function fetchAsDataUrl(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Could not fetch generated image: ${res.status}`);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

// ---------- Deterministic mock generators (no API key required) ----------

function hashPrompt(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h << 5) - h + s.charCodeAt(i) | 0;
  return Math.abs(h);
}

function mockPatternForPrompt(prompt: string, style: StylePresetId | null): string {
  const h = hashPrompt(prompt + (style ?? ''));
  const variant = h % 4;
  const a = (h % 12) + 4;
  const b = ((h >> 4) % 16) + 6;
  const r = ((h >> 8) % 5) + 4;
  const inner = [
    // Variant 0: mandala dots
    `<g fill="white">
      ${Array.from({ length: 8 }, (_, i) => {
        const ang = (i * Math.PI * 2) / 8;
        const x = 32 + Math.cos(ang) * a;
        const y = 32 + Math.sin(ang) * a;
        return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${r}"/>`;
      }).join('')}
      <circle cx="32" cy="32" r="${r + 2}"/>
    </g>`,
    // Variant 1: woven lines
    `<g fill="white">
      ${Array.from({ length: 4 }, (_, i) => {
        const y = 4 + i * 14 + (h % 4);
        return `<rect x="0" y="${y}" width="64" height="${b / 2}"/>`;
      }).join('')}
    </g>`,
    // Variant 2: chevron
    `<g fill="none" stroke="white" stroke-width="${(b / 4).toFixed(0)}">
      <polyline points="0,12 16,4 32,12 48,4 64,12"/>
      <polyline points="0,28 16,20 32,28 48,20 64,28"/>
      <polyline points="0,44 16,36 32,44 48,36 64,44"/>
      <polyline points="0,60 16,52 32,60 48,52 64,60"/>
    </g>`,
    // Variant 3: triangles
    `<g fill="white">
      ${Array.from({ length: 4 }, (_, i) => {
        const cx = 8 + (i * 16);
        return `<polygon points="${cx},${4 + (i % 2) * 32} ${cx + 12},${28 + (i % 2) * 32} ${cx - 12},${28 + (i % 2) * 32}"/>`;
      }).join('')}
    </g>`
  ][variant];

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64">
    <rect width="64" height="64" fill="#808080"/>
    ${inner}
  </svg>`;
}

function mockPatternForImage(name: string, extra: string, style: StylePresetId | null): string {
  return mockPatternForPrompt(`img:${name}|${extra}`, style);
}
